import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import notificationService from "../services/notificationService";
import { useDashboardUI } from "../context/GlobalContext";
import {
  CATEGORY_META,
  STATUS_META,
  SEVERITY_META,
  pillStyle,
  formatUtcToLocal,
} from "../helpers/notificationMeta";

// Phones/tablets: long bodies are clamped with a Read more / Read less toggle;
// from lg up the full text is shown as before and the toggle is hidden.
const ReadMoreBody = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <p
        className={`text-sm text-gray-700 whitespace-pre-wrap ${
          expanded ? "" : "line-clamp-4 lg:line-clamp-none"
        }`}
      >
        {text}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-xs font-semibold text-[#3F4BB8] hover:underline lg:hidden"
      >
        {expanded ? "Read less" : "Read more"}
      </button>
    </div>
  );
};

const NotificationDetail = () => {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const { facilityList } = useDashboardUI();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchNotification = useCallback(async () => {
    try {
      const data = await notificationService.getNotification(notificationId);
      setNotification(data);
    } catch (err) {
      toast.error("Failed to load notification");
      navigate("/notifications");
    } finally {
      setLoading(false);
    }
  }, [notificationId, navigate]);

  useEffect(() => {
    if (!notificationId) {
      navigate("/notifications");
      return;
    }
    fetchNotification();
  }, [notificationId, fetchNotification, navigate]);

  const runAction = async (action, successMessage) => {
    setActionLoading(true);
    try {
      await action();
      toast.success(successMessage);
      await fetchNotification();
    } catch (err) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = () =>
    runAction(() => notificationService.publishNotification(notificationId), "Notification published");
  const handleArchive = () =>
    runAction(() => notificationService.archiveNotification(notificationId), "Notification archived");
  const handleProcessDeprecation = () =>
    runAction(() => notificationService.processDeprecation(notificationId), "Deprecation processed");
  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await notificationService.deleteNotification(notificationId);
      toast.success("Draft deleted");
      navigate("/notifications");
    } catch (err) {
      toast.error("Failed to delete notification");
      setActionLoading(false);
    }
  };

  const audienceLabel = () => {
    if (!notification) return "—";
    if (notification.audience_type === "ALL") return "All facilities";
    const ids = notification.target_facility_ids || [];
    if (ids.length === 0) return "0 facilities";
    const names = ids.map((id) => {
      const facility = (facilityList || []).find((f) => f.facility_number === id);
      return facility?.name || `#${id}`;
    });
    return names.join(", ");
  };

  if (loading) {
    return (
      <div className="w-full rounded-2xl bg-white p-6 flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3674]" />
      </div>
    );
  }

  if (!notification) return null;

  const categoryMeta = CATEGORY_META[notification.category] || {};
  const statusMeta = STATUS_META[notification.status] || {};
  const severityMeta = SEVERITY_META[notification.severity] || {};
  const CategoryIcon = categoryMeta.icon;

  return (
    <div className="w-full rounded-2xl bg-white p-4 sm:p-6 flex flex-col h-auto min-h-[calc(100vh-120px)] lg:h-[calc(100vh-120px)] overflow-y-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 flex-shrink-0 min-w-0">
        <button
          onClick={() => navigate("/notifications")}
          className="flex flex-shrink-0 items-center gap-1 hover:text-gray-700 transition"
        >
          <ArrowLeft size={16} />
          Notifications
        </button>
        <span>/</span>
        <span className="text-gray-800 font-medium line-clamp-1 min-w-0">{notification.title}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6 flex-shrink-0">
        <div className="flex flex-col gap-2 min-w-0">
          <h1 className="text-xl font-semibold text-gray-800 break-words">{notification.title}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <span style={pillStyle(categoryMeta.color || "#6b7280")}>
              {CategoryIcon && <CategoryIcon size={12} />}
              {categoryMeta.label || notification.category}
            </span>
            <span style={pillStyle(statusMeta.color || "#6b7280")}>
              {statusMeta.label || notification.status}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:flex-shrink-0 [&>button]:whitespace-nowrap">
          {(notification.status === "draft" || notification.status === "scheduled") && (
            <button
              onClick={handlePublish}
              disabled={actionLoading}
              className="px-5 py-2.5 text-sm font-medium rounded-full bg-[#2B3674] text-white hover:bg-[#232d61] transition disabled:opacity-50"
            >
              Publish now
            </button>
          )}
          {notification.status === "published" && (
            <button
              onClick={handleArchive}
              disabled={actionLoading}
              className="px-5 py-2.5 text-sm font-medium rounded-full border border-gray-300 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Archive
            </button>
          )}
          {notification.category === "model_deprecation" && notification.status === "published" && (
            <button
              onClick={handleProcessDeprecation}
              disabled={actionLoading}
              className="px-5 py-2.5 text-sm font-medium rounded-full border border-gray-300 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Process deprecation now
            </button>
          )}
          {notification.status === "draft" && (
            <button
              onClick={handleDelete}
              disabled={actionLoading}
              className="px-5 py-2.5 text-sm font-medium rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        {/* Main content */}
        <div className="lg:col-span-2 flex flex-col gap-4 min-w-0 break-words">
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Body</h3>
            {notification.body ? (
              <ReadMoreBody text={notification.body} />
            ) : (
              <p className="text-sm text-gray-400">No body content.</p>
            )}
          </div>

          {notification.link && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Link</h3>
              <a
                href={notification.link}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:underline break-all"
              >
                {notification.link}
              </a>
            </div>
          )}

          {notification.category === "model_deprecation" && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Affected models
              </h3>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(notification.affected_model_names || notification.affected_models || []).map((name, i) => (
                  <span
                    key={`${name}-${i}`}
                    className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700"
                  >
                    {name}
                  </span>
                ))}
                {(!notification.affected_models || notification.affected_models.length === 0) && (
                  <span className="text-sm text-gray-400">None</span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Deactivating on{" "}
                <span className="font-medium text-gray-700">
                  {formatUtcToLocal(notification.deprecation_at, "LLL d, yyyy h:mm a")}
                </span>
              </p>
            </div>
          )}

          {notification.category === "release_note" && notification.version && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Version</h3>
              <p className="text-sm text-gray-700">{notification.version}</p>
            </div>
          )}

          {notification.category === "emergency" && (
            <div className="flex items-center gap-2">
              <span style={pillStyle(severityMeta.color || "#6b7280")}>
                {severityMeta.label || notification.severity}
              </span>
              <span className="text-xs text-gray-500">
                {notification.dismissible === false ? "Not dismissible" : "Dismissible"}
              </span>
            </div>
          )}
        </div>

        {/* Sidebar meta */}
        <div className="flex flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 h-fit min-w-0 [&>div]:gap-4 [&>div>span:first-child]:shrink-0 [&>div>span:last-child]:text-right [&>div>span:last-child]:break-words [&>div>span:last-child]:min-w-0">
          <div className="flex justify-between text-sm gap-4">
            <span className="text-gray-500 shrink-0">Audience</span>
            <span className="font-medium text-gray-800 text-right">{audienceLabel()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Created</span>
            <span className="font-medium text-gray-800">{formatUtcToLocal(notification.created_at, "LLL d, yyyy h:mm a")}</span>
          </div>
          {notification.scheduled_for && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Scheduled for</span>
              <span className="font-medium text-gray-800">
                {formatUtcToLocal(notification.scheduled_for, "LLL d, yyyy h:mm a")}
              </span>
            </div>
          )}
          {notification.published_at && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Published</span>
              <span className="font-medium text-gray-800">{formatUtcToLocal(notification.published_at, "LLL d, yyyy h:mm a")}</span>
            </div>
          )}
          {notification.expires_at && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Expires</span>
              <span className="font-medium text-gray-800">{formatUtcToLocal(notification.expires_at, "LLL d, yyyy h:mm a")}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDetail;
